import { useEffect, useState } from 'react';

const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return false;
    }

    return window.matchMedia(REDUCED_MOTION_MEDIA_QUERY).matches;
  });

  useEffect(() => {
    if (
      typeof window === 'undefined' ||
      typeof window.matchMedia !== 'function'
    ) {
      return;
    }

    const mediaQueryList = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);
    const updatePreference = (event?: MediaQueryListEvent | MediaQueryList) => {
      setPrefersReducedMotion(event?.matches ?? mediaQueryList.matches);
    };

    updatePreference(mediaQueryList);

    if (typeof mediaQueryList.addEventListener === 'function') {
      mediaQueryList.addEventListener('change', updatePreference);

      return () => {
        mediaQueryList.removeEventListener('change', updatePreference);
      };
    }

    mediaQueryList.addListener(updatePreference);

    return () => {
      mediaQueryList.removeListener(updatePreference);
    };
  }, []);

  return prefersReducedMotion;
}

export function useSheetPresence(isOpen: boolean) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isMounted, setIsMounted] = useState(isOpen);

  useEffect(() => {
    if (isOpen) {
      setIsMounted(true);
      return;
    }

    if (!isMounted) {
      return;
    }

    if (prefersReducedMotion) {
      setIsMounted(false);
    }
  }, [isMounted, isOpen, prefersReducedMotion]);

  const handleCloseAnimationEnd = () => {
    if (isOpen) {
      return;
    }

    setIsMounted(false);
  };

  return {
    handleCloseAnimationEnd,
    isClosing: isMounted && !isOpen,
    isMounted,
  };
}
